/**
 * Audio Configuration for Iron Meridian
 * 
 * Defines volume levels, categories, timing constants, and sound mappings.
 * All audio is either procedurally generated or CC0/Public Domain.
 */

// =====================================================================
// VOLUME DEFAULTS (0.0 - 1.0)
// =====================================================================

export const DEFAULT_VOLUMES = {
  master: 0.7,
  ui: 0.4,      // Slightly reduced for softer clicks
  events: 0.6,
  ambient: 0.2, // Lower for less fatigue during long sessions
  music: 0.35,  // Slightly reduced for gentler background
};

// =====================================================================
// AUDIO CATEGORIES
// =====================================================================

export const AUDIO_CATEGORIES = {
  UI: 'ui',
  EVENTS: 'events',
  AMBIENT: 'ambient',
  MUSIC: 'music',
};

// =====================================================================
// UI SOUND DEFINITIONS
// =====================================================================

export const UI_SOUNDS = {
  CLICK: 'click',
  HOVER: 'hover',
  CONFIRM: 'confirm',
  CANCEL: 'cancel',
  ALERT: 'alert',
};

// =====================================================================
// GAME EVENT SOUND DEFINITIONS
// =====================================================================

export const EVENT_SOUNDS = {
  END_TURN: 'endTurn',
  COMBAT_INITIATED: 'combatInitiated',
  COMBAT_VICTORY: 'combatVictory',
  COMBAT_DEFEAT: 'combatDefeat',
  COMBAT_STALEMATE: 'combatStalemate',  // Added for distinct stalemate feedback
  TERRITORY_CAPTURED: 'territoryCaptured',
  TERRITORY_LOST: 'territoryLost',      // Added for territory loss feedback
  PLAYER_ELIMINATED: 'playerEliminated',
  RESOURCE_GAINED: 'resourceGained',
  TECH_UNLOCKED: 'techUnlocked',
};

// =====================================================================
// AMBIENT SOUND DEFINITIONS
// =====================================================================

export const AMBIENT_SOUNDS = {
  WAR_ROOM: 'warRoom',
  TENSION: 'tension',
};

// =====================================================================
// TIMING CONSTANTS (milliseconds)
// =====================================================================

export const AUDIO_TIMING = {
  // Debounce delay to prevent sound spam
  DEBOUNCE_MS: 50,
  
  // Fade durations
  FADE_IN_MS: 500,
  FADE_OUT_MS: 300,
  
  // Duck ambient/music during events
  DUCK_DURATION_MS: 1500,
  DUCK_LEVEL: 0.3, // Reduce to 30% during events
  
  // Minimum time between same sound
  MIN_REPEAT_INTERVAL_MS: 100,
};

// =====================================================================
// PROCEDURAL SOUND PARAMETERS
// =====================================================================

export const PROCEDURAL_PARAMS = {
  // UI Click
  click: {
    frequency: 1200,
    duration: 0.08,
    type: 'sine',
    attack: 0.005,
    decay: 0.075,
  },
  
  // UI Hover
  hover: {
    frequency: 800,
    duration: 0.04,
    type: 'sine',
    attack: 0.002,
    decay: 0.038,
    gain: 0.3,
  },
  
  // Confirm action
  confirm: {
    frequencies: [523.25, 659.25, 783.99], // C5, E5, G5 major chord arpeggio
    duration: 0.15,
    type: 'sine',
    stagger: 0.05,
  },
  
  // Cancel action
  cancel: {
    frequency: 300,
    endFrequency: 200,
    duration: 0.12,
    type: 'sine',
  },
  
  // Alert notification
  alert: {
    frequency: 880,
    duration: 0.1,
    type: 'triangle',
    pulses: 2,
    pulseGap: 0.12,
  },
  
  // End turn whoosh
  endTurn: {
    startFrequency: 200,
    endFrequency: 600,
    duration: 0.3,
    type: 'sine',
    noiseAmount: 0.1,
  },
  
  // Combat initiated
  combatInitiated: {
    frequency: 150,
    duration: 0.4,
    type: 'sawtooth',
    harmonics: [1, 0.5, 0.25],
  },
  
  // Victory fanfare (ascending)
  combatVictory: {
    frequencies: [392, 493.88, 587.33, 783.99], // G4, B4, D5, G5
    duration: 0.2,
    type: 'sine',
    stagger: 0.08,
  },
  
  // Defeat (descending minor)
  combatDefeat: {
    frequencies: [392, 349.23, 293.66, 220], // G4, F4, D4, A3
    duration: 0.25,
    type: 'sine',
    stagger: 0.1,
  },
  
  // Territory captured
  territoryCaptured: {
    frequencies: [440, 554.37, 659.25], // A4, C#5, E5
    duration: 0.18,
    type: 'triangle',
    stagger: 0.06,
  },
  
  // Player eliminated (dramatic low)
  playerEliminated: {
    frequency: 110,
    endFrequency: 55,
    duration: 0.8,
    type: 'sawtooth',
  },
  
  // Resource gained
  resourceGained: {
    frequency: 1047,
    duration: 0.1,
    type: 'sine',
    pulses: 1,
  },
  
  // Tech unlocked
  techUnlocked: {
    frequencies: [523.25, 659.25, 783.99, 1046.5], // C5, E5, G5, C6
    duration: 0.12,
    type: 'sine',
    stagger: 0.04,
  },
};

// =====================================================================
// AMBIENT PARAMETERS
// =====================================================================

export const AMBIENT_PARAMS = {
  warRoom: {
    // Low rumble base
    baseFrequency: 60,
    // Subtle radio static
    staticAmount: 0.02,
    // Occasional blips
    blipInterval: [3000, 8000], // Random interval range in ms
    blipFrequency: [400, 800],
    blipDuration: 0.05,
    // Volume envelope
    fadeInDuration: 2000,
    fadeOutDuration: 1500,
  },
  
  tension: {
    // Drone base
    droneFrequency: 80,
    // Slow oscillation
    lfoFrequency: 0.1,
    lfoDepth: 10,
    // Subtle dissonance
    detuneAmount: 5,
    // Volume
    fadeInDuration: 3000,
    fadeOutDuration: 2000,
  },
};

// =====================================================================
// LOCAL STORAGE KEY
// =====================================================================

export const STORAGE_KEY = 'ironMeridian_audioSettings';

// =====================================================================
// HELPER: Load saved settings or defaults
// =====================================================================

export function loadAudioSettings() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      return {
        ...DEFAULT_VOLUMES,
        ...parsed,
        muted: parsed.muted ?? false,
      };
    }
  } catch (e) {
    console.warn('[AudioConfig] Failed to load saved settings:', e);
  }
  return { ...DEFAULT_VOLUMES, muted: false };
}

// =====================================================================
// HELPER: Save settings to localStorage
// =====================================================================

export function saveAudioSettings(settings) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch (e) {
    console.warn('[AudioConfig] Failed to save settings:', e);
  }
}
